import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { Search, Copy, Check, Download, Upload, Maximize2, Minimize2, RotateCcw, ChevronRight, AlertCircle } from 'lucide-react';
import Button from './Button';
import { toast } from 'sonner';

// JSON Schema для валидации структуры Script
const SCRIPT_SCHEMA = {
  type: 'object',
  properties: {
    Settings: {
      type: 'object',
      properties: {
        Bot_Role: { type: 'string' },
        Notifications: { type: 'array' }
      }
    },
    Main_Script: {
      type: 'object',
      properties: {
        Stages: {
          type: 'array',
          items: {
            type: 'object',
            required: ['Stage_Name', 'Stage_Number'],
            properties: {
              UUID: { type: 'string' },
              Stage_Name: { type: 'string' },
              Stage_Number: { type: 'string' },
              Stage_Description: { type: 'string' },
              Stage_Skip_If: { type: 'string' },
              Stage_Vars: {
                type: 'array',
                items: {
                  type: 'object',
                  required: ['Stage_Name'],
                  properties: {
                    UUID: { type: 'string' },
                    Name_ID: { type: 'string' },
                    Stage_Name: { type: 'string' },
                    Stage_Var_If: { type: 'string' },
                    Steps: {
                      type: 'array',
                      items: {
                        type: 'object',
                        required: ['Step_Name', 'Step_Number'],
                        properties: {
                          UUID: { type: 'string' },
                          Name_ID: { type: 'string' },
                          Step_Name: { type: 'string' },
                          Step_Number: { type: 'string' },
                          Step_Description: { type: 'string' },
                          Step_Skip_If: { type: 'string' },
                          Step_Vars: { type: 'array' }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    },
    Script_For_Incidents: { type: 'object' },
    Followups_For_Scripts: { type: 'object' },
    Coordinating_Settings: { type: 'object' },
    All_Statuses: { type: 'object' }
  }
};

const AdvancedJsonEditor = ({ value, onChange, height = '600px', readOnly = false, enableSchemaValidation = true }) => {
  const [editorValue, setEditorValue] = useState('');
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stickyContext, setStickyContext] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const containerRef = useRef(null);
  const isInternalChangeRef = useRef(false);

  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    
    try {
      const formatted = typeof value === 'string' 
        ? JSON.stringify(JSON.parse(value), null, 2)
        : JSON.stringify(value, null, 2);
      setEditorValue(formatted);
    } catch (err) {
      console.error('Error formatting JSON:', err);
      setEditorValue(typeof value === 'string' ? value : '');
    }
  }, [value]);

  const handleEditorChange = (newValue) => {
    setEditorValue(newValue);
    
    if (!onChange) return;
    
    try {
      const parsed = JSON.parse(newValue);
      isInternalChangeRef.current = true;
      onChange(parsed);
    } catch (err) {
      // Не вызываем onChange если JSON невалиден
    }
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Настройка Monaco Editor для JSON с Schema Validation
    const schemas = enableSchemaValidation ? [{
      uri: 'http://myserver/script-schema.json',
      fileMatch: ['*'],
      schema: SCRIPT_SCHEMA
    }] : [];

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      schemas: schemas,
      enableSchemaRequest: false,
    });

    // Отслеживание прокрутки для Sticky Context
    editor.onDidScrollChange(() => {
      updateStickyContext(editor);
    });

    // Отслеживание позиции курсора для Sticky Context
    editor.onDidChangeCursorPosition(() => {
      updateStickyContext(editor);
    });

    // Команда для форматирования (переопределяем на Ctrl+Shift+F для совместимости с Find)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
      editor.getAction('editor.action.formatDocument').run();
    });

    // Установка фокуса
    editor.focus();
  };

  // Обновление Sticky Context на основе видимых строк
  const updateStickyContext = (editor) => {
    try {
      const model = editor.getModel();
      const content = model.getValue();
      const visibleRanges = editor.getVisibleRanges();
      
      if (visibleRanges.length === 0) {
        setStickyContext(null);
        return;
      }
      
      const firstVisibleLine = visibleRanges[0].startLineNumber;
      const jsonObj = JSON.parse(content);
      const offset = model.getOffsetAt({ lineNumber: firstVisibleLine, column: 1 });
      
      // Находим контекст текущей позиции
      const context = findContextAtOffset(content, offset, jsonObj);
      setStickyContext(context);
    } catch (err) {
      // Игнорируем ошибки парсинга
    }
  };

  // Найти контекст (Stage, Step) по offset в JSON
  const findContextAtOffset = (text, offset, jsonObj) => {
    const upToOffset = text.substring(0, offset);
    const lines = upToOffset.split('\n');
    
    let currentStage = null;
    let currentStageVar = null;
    let currentStep = null;
    
    // Простой поиск по ключевым словам в предыдущих строках
    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i];
      
      // Ищем Stage_Name
      const stageNameMatch = line.match(/"Stage_Name"\s*:\s*"([^"]+)"/);
      if (stageNameMatch && !currentStage) {
        currentStage = stageNameMatch[1];
      }
      
      // Ищем Name_ID для Stage_Vars
      const nameIdMatch = line.match(/"Name_ID"\s*:\s*"([^"]+)"/);
      if (nameIdMatch && !currentStageVar) {
        currentStageVar = nameIdMatch[1];
      }
      
      // Ищем Step_Name
      const stepNameMatch = line.match(/"Step_Name"\s*:\s*"([^"]+)"/);
      if (stepNameMatch && !currentStep) {
        currentStep = stepNameMatch[1];
      }
      
      // Если нашли всё - прерываем
      if (currentStage && (currentStageVar || currentStep)) break;
    }
    
    if (!currentStage && !currentStageVar && !currentStep) return null;
    
    return {
      stageName: currentStage,
      nameId: currentStageVar,
      stepName: currentStep
    };
  };

  const handleCopy = () => {
    if (editorRef.current) {
      const text = editorRef.current.getValue();
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('JSON скопирован в буфер обмена');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFormat = () => {
    if (editorRef.current) {
      try {
        const currentValue = editorRef.current.getValue();
        const parsed = JSON.parse(currentValue);
        const formatted = JSON.stringify(parsed, null, 2);
        editorRef.current.setValue(formatted);
        toast.success('JSON отформатирован');
      } catch (err) {
        toast.error('Невозможно отформатировать: невалидный JSON');
      }
    }
  };

  const handleFindReplace = () => {
    if (editorRef.current) {
      // Открываем диалог Find & Replace (Ctrl+H)
      editorRef.current.getAction('editor.action.startFindReplaceAction').run();
    }
  };

  const handleFoldLevel = (level) => {
    if (editorRef.current && monacoRef.current) {
      const editor = editorRef.current;
      
      // Сначала разворачиваем всё
      editor.getAction('editor.unfoldAll').run();
      
      // Используем setTimeout чтобы дать время на разворачивание
      setTimeout(() => {
        // Затем сворачиваем до нужного уровня используя встроенную команду Monaco
        const action = editor.getAction(`editor.foldLevel${level}`);
        if (action) {
          action.run();
          toast.success(`Свёрнуто до уровня ${level}`);
        } else {
          // Альтернативный метод через programmatic folding
          const model = editor.getModel();
          const lineCount = model.getLineCount();
          
          for (let line = 1; line <= lineCount; line++) {
            const lineContent = model.getLineContent(line);
            const indent = lineContent.search(/\S/);
            
            // Сворачиваем строки, у которых отступ >= level * 2 (2 пробела на уровень)
            if (indent >= level * 2 && (lineContent.includes('{') || lineContent.includes('['))) {
              editor.setPosition({ lineNumber: line, column: 1 });
              editor.getAction('editor.fold').run();
            }
          }
          toast.success(`Свёрнуто до уровня ${level}`);
        }
      }, 100);
    }
  };

  const handleDownload = () => {
    if (editorRef.current) {
      const text = editorRef.current.getValue();
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `script_${new Date().getTime()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('JSON файл скачан');
    }
  };

  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const text = event.target.result;
            const parsed = JSON.parse(text);
            const formatted = JSON.stringify(parsed, null, 2);
            if (editorRef.current) {
              editorRef.current.setValue(formatted);
            }
            if (onChange) {
              onChange(parsed);
            }
            toast.success('JSON файл загружен');
          } catch (err) {
            toast.error('Ошибка загрузки файла: невалидный JSON');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleReset = () => {
    try {
      const formatted = typeof value === 'string' 
        ? JSON.stringify(JSON.parse(value), null, 2)
        : JSON.stringify(value, null, 2);
      if (editorRef.current) {
        editorRef.current.setValue(formatted);
      }
      toast.success('Изменения сброшены');
    } catch (err) {
      toast.error('Ошибка сброса изменений');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`${isFullscreen ? 'h-screen bg-[#1e1e1e] p-4 flex flex-col gap-3' : 'space-y-3'}`}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            onClick={handleFindReplace}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
          >
            <Search size={14} />
            Найти и заменить
          </Button>

          <div className="h-6 w-px bg-gray-300" />

          <Button
            onClick={handleFormat}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            disabled={readOnly}
          >
            <RotateCcw size={14} />
            Форматировать
          </Button>
          
          <Button
            onClick={handleReset}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            disabled={readOnly}
          >
            <RotateCcw size={14} />
            Сбросить
          </Button>

          <div className="h-6 w-px bg-gray-300" />

          <span className="text-sm font-medium text-gray-700">Свернуть до уровня:</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(level => (
              <button
                key={level}
                onClick={() => handleFoldLevel(level)}
                className="px-2 py-1 text-xs rounded border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 transition-colors"
              >
                {level}
              </button>
            ))}
          </div>

          <div className="h-6 w-px bg-gray-300" />

          <Button
            onClick={handleCopy}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
          >
            {copied ? (
              <>
                <Check size={14} />
                Скопировано
              </>
            ) : (
              <>
                <Copy size={14} />
                Копировать
              </>
            )}
          </Button>

          <Button
            onClick={handleDownload}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
          >
            <Download size={14} />
            Скачать
          </Button>

          {!readOnly && (
            <Button
              onClick={handleUpload}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
            >
              <Upload size={14} />
              Загрузить
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={toggleFullscreen}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
          >
            {isFullscreen ? (
              <>
                <Minimize2 size={14} />
                Выход
              </>
            ) : (
              <>
                <Maximize2 size={14} />
                На весь экран
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Sticky Scroll Context */}
      {stickyContext && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 px-4 py-2 text-sm shadow-sm rounded-lg">
          <div className="flex items-center gap-3 text-gray-700">
            {stickyContext.stageName && (
              <div className="flex items-center gap-1">
                <span className="font-semibold text-blue-700">Stage:</span>
                <span className="font-medium">{stickyContext.stageName}</span>
              </div>
            )}
            {stickyContext.nameId && (
              <div className="flex items-center gap-1">
                <ChevronRight size={14} className="text-gray-400" />
                <span className="font-semibold text-indigo-700">ID:</span>
                <span className="font-mono text-xs bg-white px-2 py-1 rounded border border-indigo-200">{stickyContext.nameId}</span>
              </div>
            )}
            {stickyContext.stepName && (
              <div className="flex items-center gap-1">
                <ChevronRight size={14} className="text-gray-400" />
                <span className="font-semibold text-purple-700">Step:</span>
                <span className="font-medium">{stickyContext.stepName}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editor */}
      <div className={`border border-gray-300 rounded-lg overflow-hidden ${isFullscreen ? 'flex-1 min-h-0' : ''}`}>
        <Editor
          height={isFullscreen ? '100%' : height}
          defaultLanguage="json"
          value={editorValue}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme="vs-dark"
          options={{
            readOnly: readOnly,
            minimap: { enabled: true },
            fontSize: 13,
            lineNumbers: 'on',
            roundedSelection: true,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            wrappingIndent: 'indent',
            formatOnPaste: true,
            formatOnType: true,
            folding: true,
            foldingStrategy: 'indentation',
            showFoldingControls: 'always',
            stickyScroll: {
              enabled: true,
              maxLineCount: 5
            },
            bracketPairColorization: {
              enabled: true,
            },
            guides: {
              indentation: true,
              bracketPairs: true,
            },
            suggest: {
              showWords: true,
              showSnippets: true,
            },
            quickSuggestions: {
              strings: true,
            },
            scrollbar: {
              vertical: 'visible',
              horizontal: 'visible',
              useShadows: false,
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
          }}
        />
      </div>

      {/* Hints */}
      <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
        <div className="flex items-center gap-1">
          <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
            Ctrl/Cmd + F
          </kbd>
          <span>Найти</span>
        </div>
        <div className="flex items-center gap-1">
          <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
            Ctrl/Cmd + H
          </kbd>
          <span>Найти и заменить</span>
        </div>
        <div className="flex items-center gap-1">
          <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
            Ctrl/Cmd + Shift + F
          </kbd>
          <span>Форматировать</span>
        </div>
        <div className="flex items-center gap-1">
          <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
            Ctrl/Cmd + K, Ctrl/Cmd + 0
          </kbd>
          <span>Свернуть всё</span>
        </div>
        <div className="flex items-center gap-1">
          <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
            Ctrl/Cmd + K, Ctrl/Cmd + J
          </kbd>
          <span>Развернуть всё</span>
        </div>
        {enableSchemaValidation && (
          <div className="flex items-center gap-1 text-green-600">
            <AlertCircle size={14} />
            <span>Валидация структуры Script включена</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedJsonEditor;
